import React, { useEffect, useRef, useState } from 'react';
import ShipmentCard, { DrawerState, ShipmentCardData } from '../ShipmentCard';
import { CustomTitle } from '../../../components';

interface AnimatedShipmentCardProps {
  card: ShipmentCardData;
  toggleDrawer: (
    state: 'open' | 'close',
    name: string,
    shipmentId: string | null,
    alert?: unknown
  ) => void;
  drawerState: DrawerState;
  openShipmentId: string | null;
  onExpire: (cardId: string) => void;
}

/**
 * AnimatedShipmentCard Component
 *
 * Displays a shipment card with a 15-second countdown timer and progress bar.
 * The card fades out and expires after the countdown unless the drawer is opened.
 * When the drawer is open for this card, the countdown pauses.
 */
const AnimatedShipmentCard: React.FC<AnimatedShipmentCardProps> = ({
  card,
  toggleDrawer,
  drawerState,
  openShipmentId,
  onExpire,
}) => {
  const [timeLeft, setTimeLeft] = useState<number>(15);
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isDrawerOpenForThisCard = openShipmentId === card.id;

  const progressPercentage = (timeLeft / 15) * 100;

  // Countdown
  useEffect(() => {
    if (isDrawerOpenForThisCard || timeLeft <= 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isDrawerOpenForThisCard, timeLeft]);

  // Remove card after fade out
  useEffect(() => {
    if (timeLeft <= 0) {
      setIsVisible(false);
      setTimeout(() => onExpire(card.id), 500);
    }
  }, [timeLeft, onExpire, card.id]);

  return (
    <div
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0px)' : 'translateY(400px)',
        transition: 'all 0.8s ease-in-out',
      }}
    >
      <ShipmentCard
        card={card}
        toggleDrawer={toggleDrawer}
        drawerState={drawerState}
        openShipmentId={openShipmentId}
      />

      {timeLeft > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '16px',
            marginBottom: '16px',
          }}
        >
          <CustomTitle
            text={
              isDrawerOpenForThisCard ? 'Paused' : `Reordering in ${timeLeft}s`
            }
            fontSize='10px'
            fontWeight='400'
            lineHeight='16px'
            marginBottom={0}
            color={'#5e5e5e'}
            whiteSpace={'nowrap'}
          />
          <div style={{ width: '100%' }}>
            <div
              style={{
                height: '4px',
                backgroundColor: 'var(--gray-300)',
                borderRadius: '8px',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${progressPercentage}%`,
                  backgroundColor: 'var(--gray-600)',
                  transition: 'width 1s linear',
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnimatedShipmentCard;
